import { Emitter } from "@core/events/emitter";
import type { IEventModel, ServerEvent } from "@models/events/IEventModel";
import { isOwnAppInstance } from "@models/multiuser/appInstanceId";
import type { IProjectModel } from "@models/project/IProjectModel";

/** Unity-local (LEFT-handed, snapshot-local) pose off the grab wire. */
export interface RemoteGrabPose {
  readonly snapshotId: string;
  readonly position: readonly [number, number, number];
  readonly rotation: readonly [number, number, number, number];
}

/**
 * `NetworkGrabUpdateModel` — web port of Unity
 * `Models/Tools/NetworkGrabUpdateModel.cs`: the wire adapter for live grab
 * streaming (`grabtool_operation`, tc 18, payload `GrabtoolEventData`).
 *
 * Wire fields (JsonUtility names, GrabtoolEventData.cs:9-44):
 *   - `entitySnapshotId` — "" marks the STOP event
 *   - `snapshotPosition` {x,y,z} / `snapshotRotation` {x,y,z,w} — the entity's
 *     LOCAL pose in Unity space (literally `transform.localPosition/Rotation`)
 *   - `sync` — pose-only sync for late joiners; `reset` — ResetAll broadcast
 *     (dead send path upstream; the receive branch is kept defensively).
 *
 * Receive-side contract kept from Unity (`ExecuteGrabToolOperation`):
 * project-id filter only (no scene filter), events buffered until a scene is
 * active, out-of-order recovery (a new entity while one is held stops the
 * held one first), stop applies the FINAL pose and never persists ("computing
 * snapback here would be inaccurate — it depends on the presenter's camera").
 * Web deviations: the `uid == "Spark"` legacy-snapback stop is applied as a
 * plain final pose. Unity's receive-side ghost (translucent cyan hierarchy at
 * the original pose, gated on the receiving client's `ShouldSnapback`) is
 * driven by {@link onRemoteStart} — `GrabToolController` renders it.
 */
export class NetworkGrabUpdateModel {
  /**
   * A REMOTE grab began (`null → id` transition, before the first
   * {@link onRemotePose}). `sync: true` events never count as a start —
   * Unity's `SyncPose` applies raw with no grab session and no ghost.
   */
  readonly onRemoteStart = new Emitter<{ snapshotId: string }>();
  /** A remote pose to apply (start / drag / sync all reduce to this). */
  readonly onRemotePose = new Emitter<RemoteGrabPose>();
  /**
   * Remote grab finished. `pose` carries the sender's FINAL pose on a normal
   * stop; it's `null` on out-of-order recovery (release only, keep the last
   * streamed pose).
   */
  readonly onRemoteStop = new Emitter<{
    snapshotId: string;
    pose: RemoteGrabPose | null;
  }>();

  private m_remoteGrabbedId: string | null = null;
  private readonly m_delayedEvents: Array<Record<string, unknown>> = [];
  private readonly m_offFns: Array<() => void> = [];

  constructor(
    private readonly m_project: IProjectModel,
    private readonly m_events: IEventModel,
  ) {
    this.m_offFns.push(
      this.m_events.onEvent.on((event) => this.onEvent(event)),
      // Unity flushes its delayed-event buffer on ActiveSceneChanged.
      this.m_project.onActiveSceneChanged.on(() => this.flushDelayed()),
    );
  }

  dispose(): void {
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    this.m_delayedEvents.length = 0;
  }

  /**
   * Publish one grab event (Unity `SendNetworkEvent` / `SendStopNetworkEvent`).
   * `snapshotId === null` sends the stop marker (`entitySnapshotId: ""`) with
   * the FINAL pose. Pose is the entity's Unity-space LOCAL pos + quat.
   */
  sendGrabEvent(
    snapshotId: string | null,
    position: readonly [number, number, number],
    rotation: readonly [number, number, number, number],
    sync = false,
  ): void {
    const sceneId = this.m_project.activeSceneId;
    if (!sceneId) return;
    this.m_project.publishLiveProjectEvent("grabtool_operation", {
      entitySnapshotId: snapshotId ?? "",
      snapshotPosition: { x: position[0], y: position[1], z: position[2] },
      snapshotRotation: { x: rotation[0], y: rotation[1], z: rotation[2], w: rotation[3] },
      sync,
      reset: false,
      sceneId,
      SceneID: sceneId,
      SceneId: sceneId,
    });
  }

  private onEvent(event: ServerEvent): void {
    if (event.type.toLowerCase() !== "grabtool_operation") return;
    const payload = event.payload as Record<string, unknown> | null;
    if (!payload || typeof payload !== "object") return;
    // Own echoes: filtered by app-instance id like Unity's listener.
    if (isOwnAppInstance(payload.aid ?? payload.OriginatingAppInstanceId)) return;
    // Project filter only — Unity deliberately skips the scene check.
    const projectId =
      stringOf(payload.projectId) ?? stringOf(payload.ProjectId) ?? null;
    if (projectId && this.m_project.activeProjectId && projectId !== this.m_project.activeProjectId) {
      return;
    }
    if (!this.m_project.activeSceneId) {
      this.m_delayedEvents.push(payload);
      return;
    }
    this.execute(payload);
  }

  private flushDelayed(): void {
    if (!this.m_project.activeSceneId || this.m_delayedEvents.length === 0) return;
    const pending = this.m_delayedEvents.splice(0, this.m_delayedEvents.length);
    for (const payload of pending) this.execute(payload);
  }

  private execute(payload: Record<string, unknown>): void {
    if (payload.reset === true) {
      // Dead send path upstream (no caller of SendNetworkResetEvent);
      // scene resets propagate via bulk snapshot updates instead.
      return;
    }
    const snapshotId = stringOf(payload.entitySnapshotId) ?? "";
    const position = vec3Of(payload.snapshotPosition);
    const rotation = quatOf(payload.snapshotRotation);
    if (!position || !rotation) return;

    if (snapshotId === "") {
      // Stop: apply the sender's final pose to whichever entity was held.
      const held = this.m_remoteGrabbedId;
      this.m_remoteGrabbedId = null;
      if (held) {
        this.onRemoteStop.emit({
          snapshotId: held,
          pose: { snapshotId: held, position, rotation },
        });
      }
      return;
    }
    if (payload.sync === true) {
      // Late-joiner pose sync — apply without opening a grab session.
      this.onRemotePose.emit({ snapshotId, position, rotation });
      return;
    }
    if (this.m_remoteGrabbedId && this.m_remoteGrabbedId !== snapshotId) {
      // Out-of-order recovery: a new grab arrived while one was held —
      // release the held entity where it is (no final pose to apply).
      console.warn("[NetworkGrabUpdateModel] received grab events in wrong order");
      this.onRemoteStop.emit({ snapshotId: this.m_remoteGrabbedId, pose: null });
      this.m_remoteGrabbedId = null;
    }
    if (this.m_remoteGrabbedId === null) {
      // Start (Unity `StartGrab(entity, pos, isGhostVisible: ShouldSnapback)`).
      // Emitted BEFORE the first pose so the ghost captures the pre-drag pose.
      this.m_remoteGrabbedId = snapshotId;
      this.onRemoteStart.emit({ snapshotId });
    }
    this.onRemotePose.emit({ snapshotId, position, rotation });
  }
}

export const NETWORK_GRAB_UPDATE_MODEL = Symbol.for("Campfire.NetworkGrabUpdateModel");

function stringOf(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function vec3Of(value: unknown): readonly [number, number, number] | null {
  if (!value || typeof value !== "object") return null;
  const o = value as Record<string, unknown>;
  const x = Number(o.x);
  const y = Number(o.y);
  const z = Number(o.z);
  return Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z) ? [x, y, z] : null;
}

function quatOf(value: unknown): readonly [number, number, number, number] | null {
  if (!value || typeof value !== "object") return null;
  const o = value as Record<string, unknown>;
  const x = Number(o.x);
  const y = Number(o.y);
  const z = Number(o.z);
  const w = Number(o.w);
  return [x, y, z, w].every((n) => Number.isFinite(n)) ? [x, y, z, w] : null;
}
